import {Button, type ButtonProps} from '@famly/mf_input_button';
import i18next from 'i18next';
import {useBreakpoints} from '@famly/mf_util_breakpoints';
import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import React from 'react';

/**
 * Base version of the delete button for mobile views
 * Displays icon and text
 */
const ModalDeleteButtonBase = (props: ModalDeleteButtonProps) => {
    return (
        <Button
            fullWidth
            size="regular"
            type="button"
            variant="critical"
            text={
                i18next.t('mf.modalsDialogs_modal.src.translations.json.deleteButtonLabel') ??
                // Fallback string for storybook
                'Delete'
            }
            icon="delete"
            order={2}
            {...props}
        />
    );
};

/**
 * Tablet and desktop version of the delete button
 * Displays only delete icon
 */
const ModalDeleteButtonTablet = (props: Omit<ModalDeleteButtonProps, 'text'>) => {
    const dataAriaProps = useAriaAndDataProps(props);
    return (
        <Button
            size="compact"
            type="button"
            variant="critical"
            aria-label={i18next.t('mf.modalsDialogs_modal.src.translations.json.deleteButtonLabel')}
            icon="delete"
            order={1}
            {...props}
            {...dataAriaProps}
        />
    );
};

type ModalDeleteButtonProps = {
    text?: ButtonProps['text'];
    type?: ButtonProps['type'];
    isDisabled?: ButtonProps['isDisabled'];
    isLoading?: ButtonProps['isLoading'];
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

/**
 * ModalDeleteButton component
 *
 * A specialized button for delete actions in modals, using the critical variant.
 * Renders differently based on viewport width to optimize for each form factor:
 *
 * - On mobile: Shows both text and icon
 * - On tablet and larger:  Shows only the delete icon
 *
 * Should be placed first in ModalFooterButtons (usually on the left side).
 *
 * @param {ModalDeleteButtonProps} props - Component props
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onClick - Click handler for the delete action
 *
 * @example
 * <ModalFooterButtons>
 *   <ModalDeleteButton onClick={handleDelete} />
 *   <ModalSecondaryButton text="Cancel" onClick={handleCancel} />
 *   <ModalPrimaryButton text="Save" onClick={handleSave} />
 * </ModalFooterButtons>
 */
export const ModalDeleteButton = (props: ModalDeleteButtonProps) => {
    const {isTabletPortraitAndLarger} = useBreakpoints();

    if (isTabletPortraitAndLarger) {
        const {text, ...innerProps} = props;
        return <ModalDeleteButtonTablet {...innerProps} />;
    }

    return <ModalDeleteButtonBase {...props} />;
};
